import React, { useEffect, useState } from 'react'
import '../../components/HeaderTableComponent/style.css'
import {
  Apply,
  ButtonContainer,
  CustomCheckBox,
  CustomDegreeCheckbox,
  SearchButton,
  StatusContainer,
  StatusDot,
  StatusGroup,
  StatusText,
  TableContent,
  TableData,
  TableWrapper,
} from '../../components/HeaderTableComponent/style.js'

const HeaderTableComponent = ({
  type,
  handleApplyClick,
  checkAllCompleted,
  resetSelection,
  setResetSelection,
  setSelectedStreamList,
  selectedStreamList,
  degreeStreamList,
  setSelectedSearchType,
  alreadySetDegreeList,
  setAlreadySetDegreeList,
}) => {
  const [initialCriteriaData, setInitialCriteriaData] = useState([])
  const [selectedButton, setSelectedButton] = useState('degree')

  useEffect(() => {
    setInitialCriteriaData(degreeStreamList)
  }, [degreeStreamList])

  useEffect(() => {
    if (resetSelection) {
      setInitialCriteriaData(prevData =>
        prevData.map(degree => ({
          ...degree,
          isSelected: false,
          streams: degree.streams.map(stream => ({
            ...stream,
            isSelected: false,
            specialisations: stream.specialisations?.map(specialisation => ({
              ...specialisation,
              isSelected: false,
            })),
          })),
        }))
      )
      setAlreadySetDegreeList(prevState =>
        prevState.map(item => ({
          ...item,
          isSelected: false,
        }))
      )

      setResetSelection(false)
    }
  }, [resetSelection])

  const handleCheckboxChange = (type, degreeIndex, streamIndex = null) => {
    const updatedData = [...initialCriteriaData]
    const degreeId = updatedData[degreeIndex]?.degreeId
    const streamId = updatedData[degreeIndex]?.streams[streamIndex]?.streamId

    if (type === 'degree' && updatedData[degreeIndex]) {
      updatedData[degreeIndex].isSelected = !updatedData[degreeIndex].isSelected
      updatedData[degreeIndex].streams = updatedData[degreeIndex].streams.map(
        stream => ({
          ...stream,
          isSelected: updatedData[degreeIndex].isSelected,
        })
      )
    } else if (type === 'stream' && streamIndex !== null) {
      updatedData[degreeIndex].streams[streamIndex].isSelected =
        !updatedData[degreeIndex].streams[streamIndex].isSelected
      const allStreamsSelected = updatedData[degreeIndex].streams.every(
        stream => stream.isSelected
      )
      updatedData[degreeIndex].isSelected = allStreamsSelected
    }

    // Use setInitialCriteriaData to update state
    setInitialCriteriaData(updatedData)

    const streamList = updatedData[degreeIndex]?.streams?.filter(
      stream => stream.isSelected
    )
    const degreeData = {
      degreeId: updatedData[degreeIndex]?.degreeId,
      streams: streamList?.map(stream => stream?.streamId),
    }

    if (type === 'degree') {
      const isDegreeSelected = updatedData[degreeIndex].isSelected
      const clonedAlreadyExists = alreadySetDegreeList?.map(item => {
        if (item.degreeId === degreeId) {
          return { ...item, isSelected: isDegreeSelected }
        }
        return item
      })

      const selectedUpdatedArr = updatedData[degreeIndex]?.isSelected
        ? updatedData[degreeIndex]?.streams?.map(item => ({
            ...item,
            degreeId: updatedData[degreeIndex]?.degreeId,
            streamId: item?.streamId,
            isDraft: item?.isDraft,
            isExistingRule: item?.isExistingRule,
            isSelected: item?.isSelected,
          })) || []
        : []

      const filteredArray = selectedUpdatedArr.filter(
        item1 =>
          !clonedAlreadyExists?.some(item2 => {
            return (
              item1.degreeId === item2.degreeId &&
              item1.streamId === item2.streamId
            )
          })
      )

      const mergedArray = [...clonedAlreadyExists, ...filteredArray]

      const finalList = mergedArray.filter(item => {
        return !(!item.isSelected && !item.isDraft && !item.isExistingRule)
      })
      setAlreadySetDegreeList(finalList)
    } else {
      const clonedAlreadyExists = alreadySetDegreeList?.map(item => {
        if (item.degreeId === degreeId && item.streamId === streamId) {
          const updatedItem = { ...item, isSelected: !item.isSelected }
          return updatedItem
        }
        return item
      })

      const isExisting = clonedAlreadyExists?.some(
        item => item.degreeId === degreeId && item.streamId === streamId
      )

      const finalList = isExisting
        ? clonedAlreadyExists
        : [
            ...clonedAlreadyExists,
            {
              degreeId,
              streamId,
              isDraft: false,
              isExistingRule: false,
              isSelected: true,
            },
          ]

      setAlreadySetDegreeList(finalList)
    }

    const clonedData = selectedStreamList?.length
      ? selectedStreamList.some(item => item.degreeId === degreeData.degreeId)
        ? selectedStreamList.map(item =>
            item.degreeId === degreeData.degreeId ? degreeData : item
          )
        : [...selectedStreamList, degreeData]
      : [degreeData]

    setSelectedStreamList(clonedData)
  }

  const handleSelectAllDegrees = isChecked => {
    const clonedInitialData = [...initialCriteriaData]
    const clonedAlreadyExists = [...alreadySetDegreeList]

    // Create a flat array of streams with the desired structure
    let degreeStreamArr = clonedInitialData.flatMap(
      degree =>
        degree.streams?.map(stream => ({
          degreeId: degree.degreeId,
          isSelected: isChecked,
          streamId: stream?.streamId,
        })) || []
    )

    // Update existing items and filter 'a'
    const updatedList = clonedAlreadyExists.map(item => {
      const isExisting = degreeStreamArr.some(
        item1 =>
          item1.degreeId === item.degreeId && item1.streamId === item.streamId
      )

      if (isExisting) {
        // Remove from 'a' if it exists
        degreeStreamArr = degreeStreamArr.filter(
          item1 =>
            !(
              item1.degreeId === item.degreeId &&
              item1.streamId === item.streamId
            )
        )
        return { ...item, isSelected: isChecked } // Update selection state
      }

      return { ...item } // Return unchanged item if not found
    })

    // Mark remaining items in 'a' as not draft and not existing rule
    degreeStreamArr = degreeStreamArr.map(item => ({
      ...item,
      isDraft: false,
      isExistingRule: false,
    }))

    // Merge and filter final results
    const finalList = [...updatedList, ...degreeStreamArr].filter(
      item =>
        !(
          item.isSelected === false &&
          item.isDraft === false &&
          item.isExistingRule === false
        )
    )

    // Update state
    setAlreadySetDegreeList(finalList)
    setInitialCriteriaData(prevState =>
      prevState.map(degree => ({
        ...degree,
        isSelected: isChecked,
        streams: degree.streams.map(stream => ({
          ...stream,
          isSelected: isChecked,
        })),
      }))
    )
  }

  const handleButtonClick = buttonType => {
    console.log('Tab clicked:', buttonType)
    setSelectedButton(buttonType)
    const searchType = buttonType === 'degree' ? 'DEGREE' : 'STREAM'
    console.log('Setting searchType to:', searchType)
    setSelectedSearchType(searchType)
  }
  const isAnyCheckboxSelected = alreadySetDegreeList.some(
    item => item.isSelected
  )

  const allDegreesSelected = initialCriteriaData.every(
    degree => degree.isSelected
  )
  const someDegreesSelected = initialCriteriaData.some(
    degree => degree.isSelected
  )

  const streamCount = initialCriteriaData?.reduce((count, item) => {
    const selectedStreams = item?.streams?.filter(stream => stream?.isSelected)
    return count + (selectedStreams ? selectedStreams?.length : 0)
  }, 0)

  return (
    <TableWrapper>
      <TableContent>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <SearchButton
            selected={selectedButton === 'degree'}
            onClick={() => handleButtonClick('degree')}
          >
            Degree
          </SearchButton>
          <SearchButton
            selected={selectedButton === 'dept'}
            onClick={() => handleButtonClick('dept')}
          >
            Dept.
          </SearchButton>
        </div>

        <div>
          <StatusContainer>
            <StatusGroup>
              <StatusDot ruleAlreadySet />
              <StatusText ruleAlreadySet>Rule already set</StatusText>
            </StatusGroup>
            <StatusGroup marginRight="50px">
              <StatusDot inDraft />
              <StatusText inDraft>In Draft</StatusText>
            </StatusGroup>
          </StatusContainer>
        </div>
      </TableContent>

      <div className="table-rule-container">
        <table className="custom-rule-table">
          <thead>
            <tr>
              <th className="table_rule_header">
                <CustomDegreeCheckbox
                  checked={
                    initialCriteriaData?.length > 0 ? allDegreesSelected : false
                  }
                  indeterminate={!allDegreesSelected && someDegreesSelected}
                  onChange={({ target }) =>
                    handleSelectAllDegrees(target.checked)
                  }
                  disabled={checkAllCompleted}
                  className="rule_header_checkbox"
                  isHeader
                >
                  {type === 'DIPLOMA' ? 'DIPLOMA' : 'DEGREE'}
                </CustomDegreeCheckbox>
              </th>
              <th className="table_rule_header">
                <CustomDegreeCheckbox
                  checked={
                    initialCriteriaData?.length > 0 ? allDegreesSelected : false
                  }
                  indeterminate={
                    allDegreesSelected && initialCriteriaData?.length > 0
                      ? false
                      : initialCriteriaData?.some(degree =>
                          degree?.streams?.some(stream => stream?.isSelected)
                        )
                  }
                  onChange={({ target }) =>
                    handleSelectAllDegrees(target.checked)
                  }
                  className="rule_header_checkbox"
                  disabled={checkAllCompleted}
                  isHeader
                >
                  DEPARTMENT ({+' ' + streamCount})
                </CustomDegreeCheckbox>
              </th>
            </tr>
          </thead>
          <tbody>
            {initialCriteriaData.map((degree, degreeIndex) => (
              <React.Fragment key={degree.degreeId}>
                <tr>
                  <td>
                    <div>
                      <CustomDegreeCheckbox
                        checked={degree.isSelected}
                        indeterminate={
                          !degree.isSelected &&
                          degree.streams.some(stream => stream.isSelected)
                        }
                        onChange={() =>
                          handleCheckboxChange('degree', degreeIndex)
                        }
                      >
                        {degree.degreeShortForm}
                      </CustomDegreeCheckbox>
                    </div>
                  </td>
                  <td></td>
                </tr>
                {degree.streams.map((stream, streamIndex) => (
                  <tr key={stream.streamId}>
                    <TableData
                      isDraft={stream.isDraft}
                      isExistingRule={stream?.isExistingRule}
                      paddingLeft={'45px'}
                    >
                      {degree.degreeShortForm}
                    </TableData>
                    <TableData
                      isDraft={stream.isDraft}
                      isExistingRule={stream?.isExistingRule}
                      paddingLeft={'15px'}
                    >
                      <CustomCheckBox
                        checked={stream.isSelected}
                        onChange={() =>
                          handleCheckboxChange(
                            'stream',
                            degreeIndex,
                            streamIndex
                          )
                        }
                        isDraft={stream.isDraft}
                        isExistingRule={stream?.isExistingRule}
                      >
                        {stream.streamName}
                      </CustomCheckBox>
                    </TableData>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <ButtonContainer>
        <Apply
          text="Apply"
          onClick={handleApplyClick}
          disabled={!isAnyCheckboxSelected}
        >
          Apply
        </Apply>
      </ButtonContainer>
    </TableWrapper>
  )
}

export default HeaderTableComponent
